// src/pages/entit/grading.js
import React, { useMemo, useState } from "react";
import { Container, Row, Col, Form, Table } from "react-bootstrap";
import ReactECharts from "echarts-for-react";
import gradeData from "../../assets/data/grade.json";
import "../../App.css";

// ⭐ import DATENSTAND + export
import { DATENSTAND } from "../../config/constants";
import { exportToExcel } from "../../utils/exportExcel";


import {
  PALETTE_GRADING,
  PALETTE_GLEASON,
  PALETTE_CLARK,
} from "../../config/colors";

// --- formatters ---
const nfInt = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 });
const nfPct1 = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const fmtInt = (v) => (v == null ? "–" : nfInt.format(v));
const fmtPct = (v) => (v == null ? "–" : nfPct1.format(v));


// --- Schemas per Entität ---
const SCHEMAS = {
  grading: {
    label: "Grad",
    order: ["G1", "Low","G2","Medium", "G3/4/High", "GX"],
    colors: PALETTE_GRADING,
  },
  gleason: {
    label: "Gleason",
    order: ["1-6", "7a", "7b", "8", "9-10", "X"],
    colors: PALETTE_GLEASON,
  },
  clark: {
    label: "Clark-Level",
    order: ["L I", "L II", "L III", "L IV", "L V", "L X"],
    colors: PALETTE_CLARK,
  },
};

const pickSchemaForEntit = (entit) => {
  const s = String(entit || "").toLowerCase();
  if (/(prostata|prostate)/.test(s)) return SCHEMAS.gleason;
  if (/(melanom)/.test(s)) return SCHEMAS.clark;
  return SCHEMAS.grading;
};

const EXCLUDED_ENTITS = [
  "Chronische lymphatische Leukämie (CLL/SLL)",
  "Multiples Myelom",
  "Diffuses großzelliges B-Zell-Lymphom (DLBCL)",
];

const Grading = () => {
  // --- dropdowns ---
  const years = useMemo(
    () =>
      [...new Set(gradeData.map((d) => d.diayear))].sort((a, b) =>
        a > b ? 1 : -1
      ),
    []
  );

  // 🔹 show all entities (no filter here)
  const entits = useMemo(
    () =>
      [...new Set(gradeData.map((d) => d.entit))].sort((a, b) =>
        a.localeCompare(b, "de")
      ),
    []
  );

  const [year, setYear] = useState(
  years.includes(2023) ? "2023" : String(years[years.length - 1] ?? "")
);
  const [entit, setEntit] = useState(entits[0] ?? "");

  const isExcluded = EXCLUDED_ENTITS.includes(entit);

  const schema = useMemo(() => pickSchemaForEntit(entit), [entit]);

  const filtered = useMemo(
    () =>
      gradeData.filter(
        (d) => String(d.diayear) === String(year) && d.entit === entit
      ),
    [year, entit]
  );

  const maleRows = useMemo(
    () => filtered.filter((d) => d.SEX === "M"),
    [filtered]
  );
  const femaleRows = useMemo(
    () => filtered.filter((d) => d.SEX === "W"),
    [filtered]
  );

  const totalM = useMemo(
    () => maleRows.reduce((s, r) => s + (Number(r.n) || 0), 0),
    [maleRows]
  );
  const totalW = useMemo(
    () => femaleRows.reduce((s, r) => s + (Number(r.n) || 0), 0),
    [femaleRows]
  );
  const totalAll = totalM + totalW;

  const pieDataFor = (rows) =>
    rows
      .filter((r) => r.pct != null)
      .sort(
        (a, b) =>
          schema.order.indexOf(a.GRAD2) - schema.order.indexOf(b.GRAD2)
      )
      .map((r) => ({
        name: r.GRAD2,
        value: Number(r.pct),
        n: Number(r.n),
        color: schema.colors[r.GRAD2] || "#bbb",
      }));

  const malePie = useMemo(() => pieDataFor(maleRows), [maleRows, schema]);
  const femalePie = useMemo(() => pieDataFor(femaleRows), [femaleRows, schema]);

  const basePieOption = {
    tooltip: {
      trigger: "item",
      backgroundColor: "#000",
      borderWidth: 0,
      textStyle: { color: "#fff", fontSize: 12 },
      confine: true,
      formatter: (p) =>
        `<div style="text-align:left;line-height:1.3">
          <div style="font-weight:600;margin-bottom:4px">${schema.label} ${p.name}</div>
          <div>Anzahl: ${fmtInt(p.data?.n)}</div>
          <div>Anteil: ${fmtPct(p.value)}%</div>
        </div>`,
    },
    legend: { show: false },
    series: [],
  };

  const menOption = {
    ...basePieOption,
    series: [
      {
        name: "Männer",
        type: "pie",
        radius: ["40%", "70%"],
        center: ["50%", "50%"],
        minShowLabelAngle: 2,
        label: {
          show: true,
          formatter: (p) => `${p.name}: ${fmtPct(p.value)}%`,
          fontSize: 12,
        },
        labelLine: { show: true, length: 12, length2: 8 },
        data: malePie.map((d) => ({
          ...d,
          itemStyle: { color: d.color, borderWidth: 1, borderColor: "#fff" },
        })),
      },
    ],
  };

  const womenOption = {
    ...basePieOption,
    series: [
      {
        name: "Frauen",
        type: "pie",
        radius: ["40%", "70%"],
        center: ["50%", "50%"],
        minShowLabelAngle: 2,
        label: {
          show: true,
          formatter: (p) => `${p.name}: ${fmtPct(p.value)}%`,
          fontSize: 12,
        },
        labelLine: { show: true, length: 12, length2: 8 },
        data: femalePie.map((d) => ({
          ...d,
          itemStyle: { color: d.color, borderWidth: 1, borderColor: "#fff" },
        })),
      },
    ],
  };

  const tableRows = useMemo(() => {
    const map = new Map();
    const add = (row, sexKey) => {
      const key = row.GRAD2;
      if (!map.has(key)) {
        map.set(key, {
          label: row.GRAD2,
          n_m: null,
          n_w: null,
          pct_m: null,
          pct_w: null,
        });
      }
      const rec = map.get(key);
      if (sexKey === "M") {
        rec.n_m = Number(row.n);
        rec.pct_m = Number(row.pct);
      } else {
        rec.n_w = Number(row.n);
        rec.pct_w = Number(row.pct);
      }
    };
    maleRows.forEach((r) => add(r, "M"));
    femaleRows.forEach((r) => add(r, "W"));

    const arr = [...map.values()].map((r) => {
      const n_g = (r.n_m || 0) + (r.n_w || 0);
      const pct_g = totalAll > 0 ? (n_g / totalAll) * 100 : null;
      return { ...r, n_g, pct_g };
    });

    arr.sort(
      (a, b) => schema.order.indexOf(a.label) - schema.order.indexOf(b.label)
    );
    return arr;
  }, [maleRows, femaleRows, totalAll, schema]);

  const footer = useMemo(() => {
    const n_g = totalAll;
    const n_m = totalM;
    const n_w = totalW;
    return {
      n_g,
      n_m,
      n_w,
      pct_g: 100,
      pct_m: totalM > 0 ? 100 : null,
      pct_w: totalW > 0 ? 100 : null,
    };
  }, [totalAll, totalM, totalW]);

  const legendLabel = (cat) => {
    if (schema === SCHEMAS.gleason) return `Gleason ${cat}`;
    if (schema === SCHEMAS.clark) return `Clark ${cat}`;
    return `Grad ${cat}`;
  };

  const LegendGrading = () => (
    <div style={{ fontSize: 12, margin: "6px 0 10px" }}>
      {schema.order.map((cat) => (
        <span key={cat} style={{ marginRight: 16 }}>
          <span
            style={{
              display: "inline-block",
              width: 12,
              height: 12,
              borderRadius: 3,
              backgroundColor: schema.colors[cat],
              marginRight: 6,
              verticalAlign: "middle",
            }}
          />
          {legendLabel(cat)}
        </span>
      ))}
    </div>
  );

  // ⭐ EXPORT HANDLER
  const handleExport = () => {
    if (isExcluded) {
      return;
    }

    const excelData = tableRows.map((r) => ({
      Kategorie: r.label,
      Gesamt: r.n_g ?? "–",
      Männer: r.n_m ?? "–",
      Frauen: r.n_w ?? "–",
      "Gesamt (%)": fmtPct(r.pct_g),
      "Männer (%)": fmtPct(r.pct_m),
      "Frauen (%)": fmtPct(r.pct_w),
    }));

    exportToExcel({
      filename: "grading",
      sheetName: "Grading",
      data: excelData,
      metadata: [
        ["Grading"],
        ["Krebsregister Sachsen-Anhalt https://www.kr-lsa.de"],
        [`Datenstand: ${DATENSTAND}`],
        [`Export am: ${new Date().toLocaleString("de-DE")}`],
      ],
    });
  };

  return (
    <Container className="mt-4">
      <h2 className="section-title">Grading</h2>
      <div className="section-title-underline"></div>
      <div className="aktuelles-text" style={{ marginTop: "0.5rem" }}>
        <p>
          Bei der Diagnose wird neben dem Krankheitsstadium auch der Grad der
          Zelldifferenzierung beurteilt. Das histologische Grading reicht dabei
          von G1 (gut differenziertes, bösartiges Gewebe) bis zu G4
          (undifferenziertes Gewebe). Auch das Grading liefert neben der
          Stadieneinteilung wichtige Informationen für die nachfolgende Therapie
          und die Prognose.
        </p>
      </div>

      {/* ⭐ EXPORT BUTTON */}
      <button onClick={handleExport} className="top-btn mb-3"  disabled={isExcluded}>
        📥 Daten als Excel exportieren
      </button>

      {/* Dropdowns */}
      <div
        className="d-flex align-items-center gap-3 mb-4"
        style={{ maxWidth: 720 }}
      >
        <Form.Group controlId="entitSelect" className="flex-fill">
          <Form.Label>Krebs-Auswahl:</Form.Label>
          <Form.Select
            value={entit}
            onChange={(e) => setEntit(e.target.value)}
          >
            {entits.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group controlId="yearSelect" className="flex-fill">
          <Form.Label>Jahr:</Form.Label>
          <Form.Select
            value={year}
            onChange={(e) => setYear(e.target.value)}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </div>

      {isExcluded ? (
        <p
          className="mt-3"
          style={{ fontSize: "0.95rem", color: "#555" }}
        >
          Für diese Entität liegen keine Grading-Informationen vor.
        </p>
      ) : (
        <>
          {/* Pies */}
          <Row className="mb-4">
            <Col md={6}>
              <h5>Männer</h5>
              <LegendGrading />
              <ReactECharts
                option={menOption}
                style={{ width: "100%", height: 420 }}
              />
            </Col>
            <Col md={6}>
              <h5>Frauen</h5>
              <LegendGrading />
              <ReactECharts
                option={womenOption}
                style={{ width: "100%", height: 420 }}
              />
            </Col>
          </Row>

          {/* Table */}
          <Table
            responsive
            className="zeitverlauf-table text-center align-middle"
            style={{ maxWidth: "1000px" }}
          >
            <thead>
              <tr>
                <th className="text-start">{schema.label}</th>
                <th colSpan={3}>Anzahl</th>
                <th colSpan={3}>Anteil in %</th>
              </tr>
              <tr>
                <th className="text-start"></th>
                <th>Gesamt</th>
                <th>Männer</th>
                <th>Frauen</th>
                <th>Gesamt</th>
                <th>Männer</th>
                <th>Frauen</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((r) => (
                <tr key={`grade-${r.label}`}>
                  <td className="text-start">{r.label}</td>
                  <td>{fmtInt(r.n_g)}</td>
                  <td>{fmtInt(r.n_m)}</td>
                  <td>{fmtInt(r.n_w)}</td>
                  <td>{fmtPct(r.pct_g)}</td>
                  <td>{fmtPct(r.pct_m)}</td>
                  <td>{fmtPct(r.pct_w)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="table-secondary">
                <td className="text-start fw-bold">Gesamt</td>
                <td className="fw-bold">{fmtInt(footer.n_g)}</td>
                <td className="fw-bold">{fmtInt(footer.n_m)}</td>
                <td className="fw-bold">{fmtInt(footer.n_w)}</td>
                <td className="fw-bold">{fmtPct(footer.pct_g)}</td>
                <td className="fw-bold">{fmtPct(footer.pct_m)}</td>
                <td className="fw-bold">{fmtPct(footer.pct_w)}</td>
              </tr>
            </tfoot>
          </Table>
        </>
      )}
    </Container>
  );
};

export default Grading;
